/**
 * Keeping the rail's message counts true after the list files a message, without
 * reading the folder tree again.
 *
 * Re-reading is not free on this mailbox: without LIST-STATUS every count is a
 * STATUS round trip, about four seconds for forty folders, and the mail account
 * runs one session at a time - so a refresh after each click would hold the
 * operator's NEXT click behind it. The list already knows what it just did
 * (moved an unread message from A to B), so it says so, and the rail adjusts.
 *
 * Pure: no React, no request.
 */
import type { ImapFolder } from "../../../../lib/mail";

/** A change to one folder's counts. Deltas, except `unseenTo`, which sets. */
export interface FolderCountChange {
  path: string;
  messages?: number;
  unseen?: number;
  /** An absolute unread count - "the whole folder was just marked read". */
  unseenTo?: number;
}

/** A message went from one folder to another. It carries its unread mark along. */
export function moveCounts(from: string, to: string, seen: boolean): FolderCountChange[] {
  const unread = seen ? 0 : 1;
  return [
    { path: from, messages: -1, unseen: -unread },
    { path: to, messages: 1, unseen: unread },
  ];
}

/** A message left for good. */
export function removedCounts(path: string, seen: boolean): FolderCountChange[] {
  return [{ path, messages: -1, unseen: seen ? 0 : -1 }];
}

/** A message's read mark changed. Nothing to say when it did not. */
export function seenCounts(path: string, wasSeen: boolean, nowSeen: boolean): FolderCountChange[] {
  if (wasSeen === nowSeen) return [];
  return [{ path, unseen: nowSeen ? -1 : 1 }];
}

/**
 * The folders with the changes applied.
 *
 * A count the rail never had (null - see FOLDER_COUNT_LIMIT on the server)
 * stays null: adding one to "unknown" is still unknown, and printing "1" there
 * would claim a count nobody read. Nothing goes below zero - a count that was
 * already stale must not turn into a negative number on screen.
 */
export function applyCountChanges(folders: ImapFolder[], changes: FolderCountChange[]): ImapFolder[] {
  if (!changes.length) return folders;
  return folders.map((f) => {
    const mine = changes.filter((c) => c.path === f.path);
    if (!mine.length) return f;
    let { messages, unseen } = f;
    for (const c of mine) {
      if (messages !== null && c.messages) messages = Math.max(0, messages + c.messages);
      if (c.unseenTo !== undefined) unseen = unseen === null ? null : c.unseenTo;
      else if (unseen !== null && c.unseen) unseen = Math.max(0, unseen + c.unseen);
    }
    return { ...f, messages, unseen };
  });
}
